import { environment } from '../../../environments/environment';
import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { UserService } from '@common/services/user.service';

interface LogItem {
    type: 'info' | 'error';
    message: string;
}

@Injectable({
    providedIn: 'root'
})
export class RemoteLogService {

    private logHubConnection: signalR.HubConnection | null = null;

    private queue: LogItem[] = [];
    private processorInterval: any;

    private readonly flushIntervalMs = 1000;
    private reconnectTimer: any = null;
    private retryDelayMs = 2000;
    private isProcessing = false;

    constructor(private userService: UserService) {
        this.startQueueProcessor();
        this.connect();
    }

    // -------------------------
    // CONNECT
    // -------------------------

    async connect(): Promise<boolean> {
        try {
            const state = this.logHubConnection?.state;

            // 🛑 GUARD: already connected or connecting -> do nothing
            if (
                state === signalR.HubConnectionState.Connected ||
                state === signalR.HubConnectionState.Connecting ||
                state === signalR.HubConnectionState.Reconnecting
            ) {
                return true;
            }

            // cleanup old connection if exists
            if (this.logHubConnection) {
                await this.logHubConnection.stop().catch(() => {});
                this.logHubConnection = null;
            }

            this.logHubConnection = new signalR.HubConnectionBuilder()
                .withUrl(environment.serverUrl + '/hubs/log', {
                    accessTokenFactory: () => this.userService.getAccessToken()
                })
                .configureLogging(
                    environment.production
                        ? signalR.LogLevel.Warning
                        : signalR.LogLevel.Information
                )
                .withAutomaticReconnect()
                .build();

            this.registerEvents();

            await this.logHubConnection.start();

            console.log('SignalR connected');
            this.info('Client connected');

            // reset retry delay on success
            this.retryDelayMs = 2000;

            return true;

        } catch (error) {
            console.error('SignalR connection failed:', error);
            this.scheduleReconnect();
            return false;
        }
    }

    private scheduleReconnect() {
        // prevent multiple timers stacking up
        if (this.reconnectTimer) return;

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, this.retryDelayMs);
    }

    // -------------------------
    // PUBLIC API (QUEUED)
    // -------------------------

    info(message: string) {
        this.queue.push({ type: 'info', message });
    }

    error(message: string) {
        this.queue.push({ type: 'error', message });
    }

    // -------------------------
    // QUEUE PROCESSOR
    // -------------------------

    private startQueueProcessor() {
        this.processorInterval = setInterval(async () => {
            if (this.isProcessing) {
                return;
            }

            this.isProcessing = true;

            try {
                await this.processQueue();
            } finally {
                this.isProcessing = false;
            }

        }, this.flushIntervalMs);
    }

    private async processQueue() {
        if (!this.logHubConnection) return;
        if (this.queue.length === 0) return;

        const isConnected =
            this.logHubConnection.state === signalR.HubConnectionState.Connected;

        if (!isConnected) {
            return;
        }

        // process one by one safely
        for (const i = 0; i < this.queue.length;) {
            const item = this.queue[i];

            try {
                if (item.type === 'info') {
                    await this.logHubConnection.invoke('Info', item.message);
                } else {
                    await this.logHubConnection.invoke('Error', item.message);
                }

                // remove ONLY after successful send
                this.queue.splice(i, 1);

            } catch (err) {
                console.error('Failed to send log:', err);

                // stop processing immediately
                // (don’t risk further failures)
                break;
            }
        }
    }

    // -------------------------
    // SIGNALR EVENTS
    // -------------------------

    private registerEvents() {
        if (!this.logHubConnection) return;

        this.logHubConnection.on('TotalClients', (totalClients) => {
            if (!environment.production) {
                console.log('TotalClients', totalClients);
            }
        });

        this.logHubConnection.onreconnecting(err => {
            console.warn('SignalR reconnecting...', err);
        });

        this.logHubConnection.onreconnected(id => {
            console.log('SignalR reconnected:', id);
            this.info('Client connected');
        });

        this.logHubConnection.onclose(err => {
            console.error('SignalR closed:', err);

            // retry connect
            this.connect();
        });
    }
}
